/**
 * PatrolListItem
 *
 * Individual patrol item for the patrol list sidebar.
 * Adapted from GuardCard pattern with patrol-specific display.
 *
 * Visual Design:
 * ┌────────────────────────────────────┐
 * │ ● Downtown Office                  │
 * │   2:00 PM - 4:00 PM               │
 * │   ▶ In Progress                   │
 * │   ████████░░░░ 6/10               │
 * └────────────────────────────────────┘
 */

import React, { useMemo } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ScheduleIcon from '@mui/icons-material/Schedule';
import classNames from 'classnames';
import styles from './PatrolList.module.css';

// ============================================
// Types
// ============================================

export type PatrolStatus = 'pending' | 'in_progress' | 'completed';

export interface PatrolListItemProps {
  /** Schedule assignment data */
  patrol: ScheduleAssignment;
  /** Whether this item is selected */
  isSelected?: boolean;
  /** Click handler */
  onClick?: (patrolId: number) => void;
  /** Compact mode for smaller displays */
  compact?: boolean;
}

// ============================================
// Constants
// ============================================

const STATUS_COLORS: Record<PatrolStatus, string> = {
  pending: 'grey.500',
  in_progress: 'primary.main',
  completed: 'success.main',
};

const STATUS_LABELS: Record<PatrolStatus, string> = {
  pending: 'Scheduled',
  in_progress: 'In Progress',
  completed: 'Completed',
};

// ============================================
// Utility Functions
// ============================================

/**
 * Determine patrol status from ScheduleAssignment
 */
function getPatrolStatus(patrol: ScheduleAssignment | null | undefined): PatrolStatus {
  if (!patrol) return 'pending';
  if (patrol.complete) return 'completed';
  if (patrol.started) return 'in_progress';
  return 'pending';
}

/**
 * Format time from Date object with error handling
 */
function formatTime(date: Date | string | null | undefined): string {
  if (!date) return '';
  try {
    const d = new Date(date);
    // Check for Invalid Date
    if (isNaN(d.getTime())) {
      console.error('[PatrolListItem/formatTime] Invalid date provided to formatTime:', date);
      return '';
    }
    return d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch (error) {
    console.error('[PatrolListItem/formatTime] Error formatting time:', error);
    return '';
  }
}

/**
 * Calculate checkpoint progress from patrol data
 * Note: This requires additional data that may come from the patrol detail
 */
function getCheckpointProgress(patrol: ScheduleAssignment): { completed: number; total: number } | null {
  // The ScheduleAssignment type doesn't have checkpoint counts directly
  // This would need to be fetched separately or added to the type
  // For now, return null to indicate no progress data available
  return null;
}

// ============================================
// Sub-components
// ============================================

interface StatusBadgeProps {
  status: PatrolStatus;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ status }) => {
  const Icon =
    status === 'in_progress'
      ? PlayArrowIcon
      : status === 'completed'
      ? CheckCircleIcon
      : ScheduleIcon;

  return (
    <Box
      className={styles.statusBadge}
      style={{ '--status-color': STATUS_COLORS[status] } as React.CSSProperties}
    >
      <Icon className={styles.statusIcon} />
      <Typography variant="caption" className={styles.statusText}>
        {STATUS_LABELS[status]}
      </Typography>
    </Box>
  );
};

interface ProgressBarProps {
  completed: number;
  total: number;
  compact?: boolean;
}

const ProgressBar: React.FC<ProgressBarProps> = ({ completed, total, compact = false }) => {
  if (total === 0) return null;

  const percentage = Math.round((completed / total) * 100);
  const isComplete = completed >= total;

  return (
    <Box className={classNames(styles.progressContainer, { [styles.compact]: compact })}>
      <Box className={styles.progressBarBg}>
        <Box
          className={classNames(styles.progressBarFill, { [styles.complete]: isComplete })}
          style={{ width: `${percentage}%` }}
        />
      </Box>
      <Typography
        variant="caption"
        className={classNames(styles.progressText, { [styles.complete]: isComplete })}
      >
        {completed}/{total}
      </Typography>
    </Box>
  );
};

// ============================================
// Main Component
// ============================================

const PatrolListItem: React.FC<PatrolListItemProps> = ({
  patrol,
  isSelected = false,
  onClick,
  compact = false,
}) => {
  const status = useMemo(() => getPatrolStatus(patrol), [patrol]);
  const progress = useMemo(() => getCheckpointProgress(patrol), [patrol]);

  const timeRange = useMemo(() => {
    const start = formatTime(patrol.StartTime || patrol.start_time);
    const end = formatTime(patrol.EndTime || patrol.end_time);
    if (start && end) return `${start} - ${end}`;
    if (start) return start;
    return '';
  }, [patrol]);

  const handleClick = () => {
    // Validate patrol ID exists before calling onClick
    const patrolId = patrol?.schedule_assignment_id;
    if (patrolId != null && onClick) {
      onClick(patrolId);
    }
  };

  return (
    <Box
      className={classNames(styles.listItem, {
        [styles.selected]: isSelected,
        [styles.compact]: compact,
        [styles.clickable]: !!onClick,
        [styles.inProgress]: status === 'in_progress',
        [styles.completed]: status === 'completed',
      })}
      onClick={handleClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(e) => {
        if (onClick && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      {/* Status indicator dot */}
      <Box
        className={styles.statusDot}
        style={{ backgroundColor: STATUS_COLORS[status] }}
      />

      {/* Content */}
      <Box className={styles.itemContent}>
        {/* Site name */}
        <Typography variant="body2" className={styles.siteName}>
          {patrol.cust_name || 'Unknown Location'}
        </Typography>

        {/* Address or time range */}
        <Typography variant="caption" className={styles.itemDetail}>
          {patrol.address || timeRange}
        </Typography>

        {/* Time range (if address shown above) */}
        {patrol.address && timeRange && (
          <Box className={styles.timeRow}>
            <AccessTimeIcon className={styles.timeIcon} />
            <Typography variant="caption" className={styles.timeText}>
              {timeRange}
            </Typography>
          </Box>
        )}

        {/* Status badge */}
        <StatusBadge status={status} />

        {/* Progress bar (if data available) */}
        {progress && (
          <ProgressBar
            completed={progress.completed}
            total={progress.total}
            compact={compact}
          />
        )}
      </Box>
    </Box>
  );
};

export default PatrolListItem;
